import { Action } from 'redux';
import { ApiResponse } from 'apisauce';
import { ThunkAction } from 'redux-thunk';
import { assistantService } from '../services/assistantAPI';
import { Editing, ErrorAction, OkAction } from './baseActions';
import { Assistant } from '../types/resources';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum AssistantActionType {
  SILENT_LIST_ASSISTANTS = 'SILENT_LIST_ASSISTANTS',
  LIST_ASSISTANTS = 'LIST_ASSISTANTS',
  LIST_ASSISTANTS_OK = 'LIST_ASSISTANTS_OK',
  LIST_ASSISTANTS_ERROR = 'LIST_ASSISTANTS_ERROR',
  SILENT_GET_ASSISTANT = 'SILENT_GET_ASSISTANT',
  GET_ASSISTANT = 'GET_ASSISTANT',
  GET_ASSISTANT_OK = 'GET_ASSISTANT_OK',
  GET_ASSISTANT_ERROR = 'GET_ASSISTANT_ERROR',
  EDIT_ASSISTANT = 'EDIT_ASSISTANT',
  EDIT_ASSISTANT_OK = 'EDIT_ASSISTANT_OK',
  EDIT_ASSISTANT_ERROR = 'EDIT_ASSISTANT_ERROR',
  DELETE_ASSISTANT = 'DELETE_ASSISTANT',
  DELETE_ASSISTANT_OK = 'DELETE_ASSISTANT_OK',
  DELETE_ASSISTANT_ERROR = 'DELETE_ASSISTANT_ERROR',
  CREATE_ASSISTANT = 'CREATE_ASSISTANT',
  CREATE_ASSISTANT_OK = 'CREATE_ASSISTANT_OK',
  CREATE_ASSISTANT_ERROR = 'CREATE_ASSISTANT_ERROR',
}

export type AssistantEditingDetails =
  | Editing<'changingName', 'changingNameError'>
  | Editing<'changingRobot', 'changingRobotError'>
  | Editing<'updatingAssistant', 'updatingAssistantError'>
  | Editing<'changingActivityStatus', 'changingActivityStatusError'>
  | Editing<'changingPermissionConfiguration', 'changingPermissionConfigurationError'>;

type WithAssistantId = { assistantId: string };

type ListAssistantsAction =
  | Action<AssistantActionType.SILENT_LIST_ASSISTANTS>
  | Action<AssistantActionType.LIST_ASSISTANTS>
  | OkAction<AssistantActionType.LIST_ASSISTANTS_OK, Assistant[]>
  | ErrorAction<AssistantActionType.LIST_ASSISTANTS_ERROR>;

type GetAssistantAction =
  | Action<AssistantActionType.SILENT_GET_ASSISTANT>
  | (Action<AssistantActionType.GET_ASSISTANT> & WithAssistantId)
  | (OkAction<AssistantActionType.GET_ASSISTANT_OK, Assistant> & WithAssistantId)
  | (ErrorAction<AssistantActionType.GET_ASSISTANT_ERROR> & WithAssistantId);

type EditAssistantAction =
  | (Action<AssistantActionType.EDIT_ASSISTANT> & WithAssistantId & AssistantEditingDetails)
  | (OkAction<AssistantActionType.EDIT_ASSISTANT_OK, Assistant> &
      WithAssistantId &
      AssistantEditingDetails)
  | (ErrorAction<AssistantActionType.EDIT_ASSISTANT_ERROR> &
      WithAssistantId &
      AssistantEditingDetails);

type DeleteAssistantAction =
  | (Action<AssistantActionType.DELETE_ASSISTANT> & WithAssistantId)
  | (OkAction<AssistantActionType.DELETE_ASSISTANT_OK, Assistant> & WithAssistantId)
  | (ErrorAction<AssistantActionType.DELETE_ASSISTANT_ERROR> & WithAssistantId);

type CreateAssistantAction =
  | Action<AssistantActionType.CREATE_ASSISTANT>
  | OkAction<AssistantActionType.CREATE_ASSISTANT_OK, Assistant>
  | ErrorAction<AssistantActionType.CREATE_ASSISTANT_ERROR>;

export type AssistantAction =
  | ListAssistantsAction
  | GetAssistantAction
  | DeleteAssistantAction
  | CreateAssistantAction
  | EditAssistantAction;

export const listAssistants =
  (): ThunkAction<void, AppState, null, ListAssistantsAction> => async (dispatch, getState) => {
    if (getState().workspaceResources.assistants.allIds.length > 1) {
      dispatch({ type: AssistantActionType.SILENT_LIST_ASSISTANTS });
    } else {
      dispatch({ type: AssistantActionType.LIST_ASSISTANTS });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantService.listAssistants({ workspaceId });
    if (response.ok) {
      dispatch({
        type: AssistantActionType.LIST_ASSISTANTS_OK,
        data: response.data as Assistant[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantActionType.LIST_ASSISTANTS_ERROR,
      });
    }
  };

export const createAssistant =
  (
    name: string,
    robotId: string,
    cmd: string,
  ): ThunkAction<void, AppState, null, CreateAssistantAction> =>
  async (dispatch, getState) => {
    dispatch({ type: AssistantActionType.CREATE_ASSISTANT });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantService.createAssistant({
      workspaceId,
      name,
      robotId,
      task: cmd,
    });
    if (response.ok) {
      dispatch({
        type: AssistantActionType.CREATE_ASSISTANT_OK,
        data: response.data as Assistant,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantActionType.CREATE_ASSISTANT_ERROR,
      });
    }
  };

export const getAssistant =
  (assistantId: string): ThunkAction<void, AppState, null, GetAssistantAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.assistants.byId[assistantId]) {
      dispatch({ type: AssistantActionType.SILENT_GET_ASSISTANT });
    } else {
      dispatch({ type: AssistantActionType.GET_ASSISTANT, assistantId });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantService.getAssistant({ workspaceId, id: assistantId });

    if (response.ok) {
      if (response.data?.id) {
        dispatch({
          type: AssistantActionType.GET_ASSISTANT_OK,
          assistantId,
          data: response.data,
        });
      } else {
        dispatch({
          type: AssistantActionType.GET_ASSISTANT_ERROR,
          assistantId,
          error: 'Incorrect API response',
        });
      }
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantActionType.GET_ASSISTANT_ERROR,
        assistantId,
      });
    }
  };

export const deleteAssistant =
  (
    assistantId: string,
  ): ThunkAction<Promise<ApiResponse<Assistant>>, AppState, null, DeleteAssistantAction> =>
  async (dispatch, getState) => {
    dispatch({ type: AssistantActionType.DELETE_ASSISTANT, assistantId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantService.deleteAssistant({ workspaceId, id: assistantId });
    if (response.ok && response.data) {
      dispatch({
        assistantId,
        type: AssistantActionType.DELETE_ASSISTANT_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantActionType.DELETE_ASSISTANT_ERROR,
        assistantId,
      });
    }

    return response;
  };

const updateAssistant =
  (
    assistantId: string,
    update: Partial<Assistant>,
    editingDetails: AssistantEditingDetails,
  ): ThunkAction<void, AppState, null, EditAssistantAction> =>
  async (dispatch, getState) => {
    const assistant = getState().workspaceResources.assistants.byId[assistantId];
    const updated = { ...assistant, ...update };

    if (typeof update.robot === 'object') {
      updated.robot = { ...assistant.robot, ...update.robot };
    }

    dispatch({
      type: AssistantActionType.EDIT_ASSISTANT,
      assistantId,
      ...editingDetails,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantService.updateAssistant({
      workspaceId,
      id: assistantId,
      assistant: updated,
    });
    if (response.ok) {
      dispatch({
        type: AssistantActionType.EDIT_ASSISTANT_OK,
        assistantId,
        data: response.data as Assistant,
        ...editingDetails,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantActionType.EDIT_ASSISTANT_ERROR,
        assistantId,
        ...editingDetails,
      });
    }
  };

export const changeAssistantName = (assistantId: string, name: string) =>
  updateAssistant(
    assistantId,
    { name },
    { statusKey: 'changingName', errorKey: 'changingNameError' },
  );

export const changeAssistantRobot = (assistantId: string, robotId: string, task: string) =>
  updateAssistant(
    assistantId,
    { robot: { id: robotId, task } },
    { statusKey: 'changingRobot', errorKey: 'changingRobotError' },
  );

export const changeAssistantNameAndRobot = (
  assistantId: string,
  robotId: string,
  task: string,
  name: string,
) =>
  updateAssistant(
    assistantId,
    { name, robot: { id: robotId, task } },
    { statusKey: 'updatingAssistant', errorKey: 'updatingAssistantError' },
  );

export const changeActivityStatus = (assistantId: string, active: boolean) =>
  updateAssistant(
    assistantId,
    { active },
    {
      statusKey: 'changingActivityStatus',
      errorKey: 'changingActivityStatusError',
    },
  );

export const changePermissionConfiguration = (
  assistantId: string,
  permissions: Assistant['permissionConfiguration'],
) =>
  updateAssistant(
    assistantId,
    { permissionConfiguration: permissions },
    {
      statusKey: 'changingPermissionConfiguration',
      errorKey: 'changingPermissionConfigurationError',
    },
  );
